/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// CSRF token'i cookie'den oku
function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : '';
}

// Request interceptor: state-changing isteklere CSRF header ekle
api.interceptors.request.use((config) => {
  const method = (config.method || '').toUpperCase();
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    config.headers['X-CSRF-Token'] = getCsrfToken();
  }
  return config;
});

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Try to refresh token
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        // Retry the original request
        return api.request(error.config);
      } catch (refreshError) {
        // Redirect to login
        window.location.href = '/giris';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  registerMuhtar: (data) => api.post('/auth/register/muhtar', data),
  registerSTK: (data) => api.post('/auth/register/stk', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  exportKvkkExcel: (params) => api.get('/users/export/kvkk-excel', { params, responseType: 'blob' }),
  exportAllExcel: (params) => api.get('/users/export/all-excel', { params, responseType: 'blob' }),
  // Toplu e-posta (KVKK uyumlu)
  sendBulkEmail: (data) => api.post('/users/bulk-email', data),
  getBulkEmailHistory: (params) => api.get('/users/bulk-email-history', { params }),
  getBulkEmailDetail: (id) => api.get(`/users/bulk-email-history/${id}`),
  // Kent Konseyi onay sureci (15 yas alti)
  listPendingKkApproval: (params) => api.get('/users/pending-kk-approval/list', { params }),
  approveKk: (id) => api.post(`/users/${id}/approve-kk`),
  rejectKk: (id) => api.post(`/users/${id}/reject-kk`),
};

// Tickets API
export const ticketsAPI = {
  create: (data) => api.post('/tickets', data),
  getAll: (params) => api.get('/tickets', { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  addComment: (id, data) => api.post(`/tickets/${id}/comments`, data),
  deleteComment: (id, commentIndex) => api.delete(`/tickets/${id}/comments/${commentIndex}`),
  deleteDiscussion: (id, discussionId) => api.delete(`/tickets/${id}/discussions/${discussionId}`),
  rateSatisfaction: (id, score) => api.post(`/tickets/${id}/satisfaction?score=${score}`),
  forward: (id, data) => api.post(`/tickets/${id}/forward`, data),
  getCategories: () => api.get('/ticket-categories'),
  getInstitutions: () => api.get('/institutions'),
  uploadFile: (id, formData) => api.post(`/tickets/${id}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadFile: (ticketId, fileId) => `${API_URL}/api/tickets/${ticketId}/files/${fileId}`,
  toggleLike: (id) => api.post(`/tickets/${id}/like`),
  addDiscussion: (id, data) => api.post(`/tickets/${id}/discussions`, data),
  bulkAction: (data) => api.post('/tickets/bulk-action', data),
  forward: (id, data) => api.post(`/tickets/${id}/forward`, data),
  returnTicket: (id, data) => api.post(`/tickets/${id}/return`, data || {}),
};

// STK API
export const stkAPI = {
  createApplication: (data) => api.post('/stk', data),
  updateApplication: (id, data) => api.put(`/stk/${id}`, data),
  submitApplication: (id) => api.post(`/stk/${id}/submit`),
  getApplications: (params) => api.get('/stk', { params }),
  getApplication: (id) => api.get(`/stk/${id}`),
  getMyDraft: () => api.get('/stk/my-draft'),
  approve: (id) => api.post(`/stk/${id}/approve`),
  reject: (id, reason) => api.post(`/stk/${id}/reject?reason=${encodeURIComponent(reason)}`),
  requestDocuments: (id, message) => api.post(`/stk/${id}/request-docs?message=${encodeURIComponent(message)}`),
  exportUrl: (status) => `${API_URL}/api/stk/export${status ? '?status=' + status : ''}`,
  bulkDownloadUrl: (status, appIds) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (appIds && appIds.length) params.set('app_ids', appIds.join(','));
    const qs = params.toString();
    return `${API_URL}/api/stk/bulk-download${qs ? '?' + qs : ''}`;
  },
  // Toplu e-posta
  sendBulkEmail: (data) => api.post('/stk/bulk-email', data),
  getBulkEmailHistory: (params) => api.get('/stk/bulk-email-history', { params }),
  getBulkEmailDetail: (id) => api.get(`/stk/bulk-email-history/${id}`),
  // Belge Talepleri (post-approval document requests)
  listDocRequests: (params) => api.get('/stk/document-requests', { params }),
  getDocRequest: (id) => api.get(`/stk/document-requests/${id}`),
  createDocRequest: (data) => api.post('/stk/document-requests', data),
  deleteDocRequest: (id) => api.delete(`/stk/document-requests/${id}`),
  myPendingDocRequests: () => api.get('/stk/document-requests/my/pending'),
  uploadDocRequestItem: (itemId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/stk/document-requests/items/${itemId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  docRequestDownloadUrl: (itemId) => `${API_URL}/api/stk/document-requests/items/${itemId}/download`,
};

// Meetings API
export const meetingsAPI = {
  create: (data) => api.post('/meetings', data),
  getAll: (params) => api.get('/meetings', { params }),
  getById: (id) => api.get(`/meetings/${id}`),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  delete: (id) => api.delete(`/meetings/${id}`),
  approve: (id) => api.post(`/meetings/${id}/approve`),
  reject: (id, reason) => api.post(`/meetings/${id}/reject`, { reason }),
};

// Announcements API
export const announcementsAPI = {
  create: (data) => api.post('/announcements', data),
  getAll: (params) => api.get('/announcements', { params }),
  getById: (id) => api.get(`/announcements/${id}`),
  update: (id, data) => api.put(`/announcements/${id}`, data),
  delete: (id) => api.delete(`/announcements/${id}`),
  approve: (id) => api.post(`/announcements/${id}/approve`),
  reject: (id, reason) => api.post(`/announcements/${id}/reject`, { reason }),
  // Attachments
  listAttachments: (id) => api.get(`/announcements/${id}/attachments`),
  uploadAttachment: (id, file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/announcements/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
  },
  deleteAttachment: (announcementId, attachmentId) =>
    api.delete(`/announcements/${announcementId}/attachments/${attachmentId}`),
  sendNotificationEmail: (id, payload) =>
    api.post(`/announcements/${id}/send-notification-email`, payload || {}),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
};

// Locations API
export const locationsAPI = {
  getIller: () => api.get('/locations/iller'),
  getIlceler: (ilId) => api.get(`/locations/ilceler/${ilId}`),
  getMahalleler: (ilceId) => api.get(`/locations/mahalleler/${ilceId}`),
};

// Organs API
export const organsAPI = {
  create: (data) => api.post('/organs', data),
  getAll: (params) => api.get('/organs', { params }),
  getTree: (params) => api.get('/organs/tree', { params }),
  getActiveDonem: () => api.get('/organs/active-donem'),
  getById: (id) => api.get(`/organs/${id}`),
  update: (id, data) => api.put(`/organs/${id}`, data),
  delete: (id) => api.delete(`/organs/${id}`),
  toggleActive: (id) => api.post(`/organs/${id}/toggle-active`),
  bulkAssignDonem: (donem_id, organ_ids) => api.post(`/organs/bulk-assign-donem?donem_id=${donem_id}`, organ_ids),
  setBaskan: (id, user_id) => api.post(`/organs/${id}/set-baskan?user_id=${user_id || ''}`),
  addMember: (id, user_id) => api.post(`/organs/${id}/add-member?user_id=${user_id}`),
  removeMember: (id, user_id) => api.post(`/organs/${id}/remove-member?user_id=${user_id}`),
  getMembers: (id) => api.get(`/organs/${id}/members`),
  searchUsers: (search) => api.get(`/organs/users/search?search=${search || ''}`),
  addApprovedMember: (id, user_id) => api.post(`/organs/${id}/approved-members/${user_id}`),
  removeApprovedMember: (id, user_id) => api.delete(`/organs/${id}/approved-members/${user_id}`),
  copyDonem: (data) => api.post('/organs/copy-donem', data),
};

// Muhtar Basvurulari API
export const muhtarApplicationsAPI = {
  list: (params) => api.get('/muhtar-applications', { params }),
  getById: (id) => api.get(`/muhtar-applications/${id}`),
  update: (id, data) => api.put(`/muhtar-applications/${id}`, data),
  approve: (user_ids) => api.post('/muhtar-applications/approve', { user_ids }),
  reject: (user_ids, reason) => api.post('/muhtar-applications/reject', { user_ids, reason }),
  exportExcel: (params) => api.get('/muhtar-applications/export/excel', { params, responseType: 'blob' }),
};

// Working Groups API
export const workingGroupsAPI = {
  getMeclisler: () => api.get('/working-groups/meclisler'),
  getMeclisDetail: (id) => api.get(`/working-groups/meclisler/${id}`),
  apply: (data) => api.post('/working-groups/apply', data),
  getMyApplications: () => api.get('/working-groups/my-applications'),
  getMyMemberships: () => api.get('/working-groups/my-memberships'),
  getAllApplications: (params) => api.get('/working-groups/applications', { params }),
  approve: (id) => api.post(`/working-groups/applications/${id}/approve`),
  reject: (id, reason) => api.post(`/working-groups/applications/${id}/reject?reason=${encodeURIComponent(reason || '')}`),
  getMyGroupDetail: (groupId) => api.get(`/working-groups/groups/${groupId}`),
  createProject: (groupId, data) => api.post(`/working-groups/groups/${groupId}/projects`, data),
  updateProject: (groupId, projectId, data) => api.put(`/working-groups/groups/${groupId}/projects/${projectId}`, data),
  deleteProject: (groupId, projectId) => api.delete(`/working-groups/groups/${groupId}/projects/${projectId}`),
  getMeclisAppCount: (meclisId) => api.get(`/working-groups/meclis/${meclisId}/application-count`),
};

// WG Engagement API (Faz 1-4)
export const wgEngageAPI = {
  getStats: (organId) => api.get(`/wg-engage/${organId}/stats`),
  // Faz 1: Tartisma
  getDiscussions: (organId) => api.get(`/wg-engage/${organId}/discussions`),
  createDiscussion: (organId, data) => api.post(`/wg-engage/${organId}/discussions`, data),
  getDiscussion: (organId, discId) => api.get(`/wg-engage/${organId}/discussions/${discId}`),
  addComment: (organId, discId, data) => api.post(`/wg-engage/${organId}/discussions/${discId}/comments`, data),
  deleteComment: (organId, discId, commentId) => api.delete(`/wg-engage/${organId}/discussions/${discId}/comments/${commentId}`),
  updateDiscussionStatus: (organId, discId, status) => api.put(`/wg-engage/${organId}/discussions/${discId}/status?status=${status}`),
  startVoteFromDiscussion: (organId, discId, data) => api.post(`/wg-engage/${organId}/discussions/${discId}/start-vote`, data),
  finalizeDiscussion: (organId, discId, data) => api.post(`/wg-engage/${organId}/discussions/${discId}/finalize`, data),
  uploadDiscussionFiles: (organId, discId, formData) => api.post(`/wg-engage/${organId}/discussions/${discId}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getDiscussionFileUrl: (organId, discId, fileId) => `${API_URL}/api/wg-engage/${organId}/discussions/${discId}/files/${fileId}`,
  deleteDiscussion: (organId, discId) => api.delete(`/wg-engage/${organId}/discussions/${discId}`),
  // Faz 1: Oylama
  getVotes: (organId) => api.get(`/wg-engage/${organId}/votes`),
  createVote: (organId, data) => api.post(`/wg-engage/${organId}/votes`, data),
  castVote: (organId, voteId, data) => api.post(`/wg-engage/${organId}/votes/${voteId}/cast`, data),
  closeVote: (organId, voteId) => api.post(`/wg-engage/${organId}/votes/${voteId}/close`),
  deleteVote: (organId, voteId) => api.delete(`/wg-engage/${organId}/votes/${voteId}`),
  // Faz 2: Kararlar
  getDecisions: (organId) => api.get(`/wg-engage/${organId}/decisions`),
  getDecision: (organId, decId) => api.get(`/wg-engage/${organId}/decisions/${decId}`),
  createDecision: (organId, data) => api.post(`/wg-engage/${organId}/decisions`, data),
  updateDecisionStatus: (organId, decId, status) => api.put(`/wg-engage/${organId}/decisions/${decId}/status?status=${status}`),
  deleteDecision: (organId, decId) => api.delete(`/wg-engage/${organId}/decisions/${decId}`),
  // Faz 2: Gorevler
  getTasks: (organId) => api.get(`/wg-engage/${organId}/tasks`),
  createTask: (organId, data) => api.post(`/wg-engage/${organId}/tasks`, data),
  updateTask: (organId, taskId, data) => api.put(`/wg-engage/${organId}/tasks/${taskId}`, data),
  deleteTask: (organId, taskId) => api.delete(`/wg-engage/${organId}/tasks/${taskId}`),
  // Faz 3: Roller
  getMemberRoles: (organId) => api.get(`/wg-engage/${organId}/member-roles`),
  setMemberRole: (organId, userId, data) => api.put(`/wg-engage/${organId}/member-roles/${userId}`, data),
  // Faz 3: Katki
  getContributions: (organId) => api.get(`/wg-engage/${organId}/contributions`),
  // Faz 4: Nabiz
  getPulses: (organId) => api.get(`/wg-engage/${organId}/pulses`),
  createPulse: (organId, data) => api.post(`/wg-engage/${organId}/pulses`, data),
  respondPulse: (organId, pulseId, data) => api.post(`/wg-engage/${organId}/pulses/${pulseId}/respond`, data),
  getPulseResponses: (organId, pulseId) => api.get(`/wg-engage/${organId}/pulses/${pulseId}/responses`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentTickets: (limit = 5) => api.get(`/dashboard/recent-tickets?limit=${limit}`),
  getRecentAnnouncements: (limit = 5) => api.get(`/dashboard/recent-announcements?limit=${limit}`),
  getUpcomingMeetings: (limit = 5) => api.get(`/dashboard/upcoming-meetings?limit=${limit}`),
  getMuhtarMahalleReport: () => api.get('/dashboard/muhtar/mahalle-report'),
};

// Meclis Baskani API
export const meclisBaskaniAPI = {
  getMyMeclis: () => api.get('/meclis-baskani/my-meclis'),
  getDashboard: () => api.get('/meclis-baskani/dashboard'),
  getTickets: (params) => api.get('/meclis-baskani/tickets', { params }),
  getWorkingGroups: () => api.get('/meclis-baskani/working-groups'),
  getReports: () => api.get('/meclis-baskani/reports'),
  createWorkingGroup: (data) => api.post('/meclis-baskani/create-working-group', data),
  getWGMembers: (wgId) => api.get(`/meclis-baskani/working-groups/${wgId}/members`),
  exportWGMembers: (wgId) => `${API_URL}/api/meclis-baskani/working-groups/${wgId}/members/export`,
  exportAll: (donemId) => `${API_URL}/api/meclis-baskani/export-all${donemId ? `?donem_id=${donemId}` : ''}`,
  addWGMember: (wgId, userId) => api.post(`/meclis-baskani/working-groups/${wgId}/add-member`, { user_id: userId }),
  removeWGMember: (wgId, userId) => api.post(`/meclis-baskani/working-groups/${wgId}/remove-member`, { user_id: userId }),
  getApplications: (params) => api.get('/meclis-baskani/applications', { params }),
  searchUsers: (search) => api.get('/meclis-baskani/users-search', { params: { search } }),
};

// File Upload API
export const filesAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (fileId) => `${API_URL}/api/files/${fileId}`,
  downloadBlob: (fileId) => api.get(`/files/${fileId}`, { responseType: 'blob' }),
};

export const rbacAPI = {
  getPermissions: () => api.get('/rbac/permissions'),
  getRoles: () => api.get('/rbac/roles'),
  getRoleDetail: (id) => api.get(`/rbac/roles/${id}`),
  createRole: (data) => api.post('/rbac/roles', data),
  updateRole: (id, data) => api.put(`/rbac/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/rbac/roles/${id}`),
  updateRolePermissions: (id, data) => api.put(`/rbac/roles/${id}/permissions`, data),
  copyRole: (data) => api.post('/rbac/roles/copy', data),
  assignUserRole: (userId, roleName) => api.put(`/rbac/users/${userId}/role?role_name=${roleName}`),
  getMyPermissions: () => api.get('/rbac/my-permissions'),
  getAuditLogs: (params) => api.get('/rbac/audit-logs', { params }),
};

export const eventsAPI = {
  getAll: (params) => api.get('/events', { params }),
  getById: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  updateStatus: (id, status) => api.put(`/events/${id}/status?status=${status}`),
  remove: (id) => api.delete(`/events/${id}`),
  apply: (id, data) => api.post(`/events/${id}/apply`, data),
  getApplications: (id) => api.get(`/events/${id}/applications`),
  updateAppStatus: (appId, data) => api.put(`/events/applications/${appId}/status`, data),
  getMyApplications: () => api.get('/events/my/applications'),
};

export const publicAPI = {
  getAnnouncements: (limit = 6) => api.get(`/public/announcements?limit=${limit}`),
  getMeetings: (limit = 6) => api.get(`/public/meetings?limit=${limit}`),
  getStats: () => api.get('/public/stats'),
  getKurumsal: () => api.get('/public/kurumsal'),
};

export const emailTemplatesAPI = {
  getAll: () => api.get('/email-templates'),
  getById: (id) => api.get(`/email-templates/${id}`),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  reset: (id) => api.post(`/email-templates/${id}/reset`),
  preview: (id) => api.post(`/email-templates/${id}/preview`),
  getLogs: (params) => api.get('/email-templates/logs/list', { params }),
  sendBulk: (data) => api.post('/email-templates/send-bulk', data),
};

export const statisticsAPI = {
  overview: (params = {}) => api.get('/statistics/overview', { params }),
  users: (params = {}) => api.get('/statistics/users', { params }),
  regional: (params = {}) => api.get('/statistics/regional', { params }),
  workingGroups: (params = {}) => api.get('/statistics/working-groups', { params }),
  proposals: (params = {}) => api.get('/statistics/proposals', { params }),
  votes: (params = {}) => api.get('/statistics/votes', { params }),
  contentViews: (params = {}) => api.get('/statistics/content-views', { params }),
  surveysEvents: (params = {}) => api.get('/statistics/surveys-events', { params }),
  activeUsers: (params = {}) => api.get('/statistics/users/active', { params }),
  exportExcelUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${api.defaults.baseURL}/statistics/export/excel${qs ? '?' + qs : ''}`;
  },
};

export const viewsAPI = {
  track: (contentType, contentId) => api.post(`/views/${contentType}/${contentId}`),
};

export default api;
